<template>
  <div>
    <div class="profile-avatar">
      <Loader
        :is-processing="state.isLoading"
        classes="position-absolute"
        :teleport-to-body="false"
      />
      <CImage
        id="profile-avatar"
        :src="user.avatar ? user.avatar : state.defaultAvatar"
        alt=""
        class="mb-2 user-avatar-image"
      />
      <div v-if="showControls && userRole !== 'student'" class="upload-btn">
        <input
          ref="fileInputRef"
          type="file"
          tabindex="-1"
          class="profile-avatar-file-input"
          :accept="acceptedProfileImageType.join(',')"
          @change="getFile"
        />
        <button
          type="button"
          class="upload-btn__trigger"
          :aria-label="$t('common.upload_profile_photo')"
          :aria-describedby="
            state.errorMessage ? 'profile-avatar-upload-error' : undefined
          "
          @click="openFilePicker"
        >
          <i class="ri-camera-line" aria-hidden="true"></i>
        </button>
      </div>
      <div
        v-if="showControls && userRole !== 'student' && user.avatar !== null"
        class="d-flex justify-content-center"
      >
        <button type="button" class="delete-image" @click="uploadAvatar(null)">
          <i class="ri-delete-bin-6-line" aria-hidden="true"></i>
          {{ $t("common.delete_image") }}
        </button>
      </div>
    </div>
    <div
      v-if="state.errorMessage"
      id="profile-avatar-upload-error"
      class="invalid-feedback d-block"
      role="alert"
    >
      {{ state.errorMessage }}
    </div>
  </div>
</template>

<script>
import Loader from "@/v3components/shared/Loader/Loader.vue"
import { reactive, ref } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

export default {
  name: "Avatar",
  components: { Loader },
  emits: ["changeAvatar"],
  props: {
    user: {
      type: Object
    },
    userRole: {
      type: String,
      default: ""
    },
    showControls: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const store = useStore()
    const { t } = useI18n()
    const state = reactive({
      defaultAvatar: "/img/avatars/user-avatar.jpeg",
      isLoading: false,
      errorMessage: ""
    })

    const fileInputRef = ref(null)

    const acceptedProfileImageType = ["image/png", "image/jpeg", "image/jpg"]

    const openFilePicker = () => {
      fileInputRef.value?.click?.()
    }

    const getFile = (event) => {
      let avatar = null
      state.errorMessage = ""
      const files = event.target.files
      avatar = files && files.length ? files[0] : avatar
      if (avatar) {
        if (avatar.size > 2048000) {
          state.errorMessage = t(
            "common.this_image_is_too_large_allowed_maximum_size_is_2mb"
          )
        } else if (!acceptedProfileImageType.includes(avatar.type)) {
          state.errorMessage = t("common.unsupported_image_type")
        } else {
          uploadAvatar(avatar)
        }
      }
      event.target.value = null
    }

    const uploadAvatar = (avatar) => {
      state.isLoading = true
      const data = {
        initials: props.user.user_initials,
        first_name: props.user.first_name,
        last_name: props.user.last_name,
        allow_appointment_requests: props.user.allow_appointment_requests,
        allow_passes_to_me: props.user.allow_passes_to_me,
        is_searchable: props.user.is_searchable
      }

      if (avatar) {
        const formData = new FormData()
        formData.append("avatar", avatar)
        for (const key in data) {
          if (Object.hasOwnProperty.call(data, key)) {
            const element = data[key]
            formData.append(key, element)
          }
        }

        updateProfile(formData, avatar)
      } else {
        store
          .dispatch("authentication/deleteUserAvatar")
          .then(() => {
            deleteImage()
            state.isLoading = false
            store.dispatch("authentication/getUserDetails", {
              clearCacheEntry: true
            })
            store.dispatch("authentication/getUserSettings", {
              clearCacheEntry: true
            })
          })
          .catch(() => {
            store.isLoading = false
          })
      }
    }

    const updateProfile = (data, avatar) => {
      store
        .dispatch("authentication/updateUserProfile", data)
        .then(() => {
          showUploadedImage(avatar)
          state.isLoading = false
          store.dispatch("authentication/getUserDetails", {
            clearCacheEntry: true
          })
          store.dispatch("authentication/getUserSettings", {
            clearCacheEntry: true
          })
        })
        .catch((err) => {
          state.isLoading = false
          const response = err.response.data
          state.errorMessage = response.message
        })
    }

    const showUploadedImage = (file) => {
      emit("changeAvatar", file)
      const avatar = document.getElementById("profile-avatar")
      avatar.src = URL.createObjectURL(file)
    }

    const deleteImage = () => {
      emit("changeAvatar", "delete")
      const avatar = document.getElementById("profile-avatar")
      avatar.src = state.defaultAvatar
    }

    return {
      state,
      fileInputRef,
      openFilePicker,
      getFile,
      uploadAvatar,
      acceptedProfileImageType
    }
  }
}
</script>
